import { useState } from "react";
import { toast } from "sonner";
import API, { formatApiError } from "../../lib/api";
import { Button } from "../ui/button";
import { Switch } from "../ui/switch";
import { fmtWhen } from "./InboxBits";
import { Gear, ArrowsClockwise, ClipboardText, Envelope, PaperPlaneTilt } from "@phosphor-icons/react";

/** Action row: 8am digest switch · Paste an email · Check mailbox now · Mailbox settings. */
export function InboxActions({ counts, pulling, onPaste, onPull, onSettings, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [sending, setSending] = useState(false);
  const [local, setLocal] = useState(null);
  const connected = counts?.connected;
  const enabled = local ?? !!counts?.digest_enabled;

  const toggleDigest = async (v) => {
    setBusy(true); setLocal(v);
    try {
      await API.put("/inbox/digest", { enabled: v });
      toast.success(v ? "You'll get the inbox digest at 8am each morning" : "8am digest switched off");
      onChanged?.();
    } catch (e) { setLocal(null); toast.error(formatApiError(e)); } finally { setBusy(false); }
  };
  const sendNow = async () => {
    setSending(true);
    try {
      const { data } = await API.post("/inbox/digest/send-now");
      toast.success(`Digest sent to ${data.to}`);
    } catch (e) { toast.error(formatApiError(e)); } finally { setSending(false); }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="inbox-actions">
      <div className={`inline-flex items-center gap-2.5 h-9 px-3 rounded-md border text-sm font-medium select-none transition-colors ${enabled ? "border-primary/60 bg-primary/5" : "border-border"}`} data-testid="inbox-digest-row">
        <Switch checked={enabled} onCheckedChange={toggleDigest} disabled={busy} aria-label="Email me the 8am digest" data-testid="inbox-digest-switch" />
        <button type="button" onClick={() => !busy && toggleDigest(!enabled)} className="cursor-pointer" data-testid="inbox-digest-label">Email me the 8am digest</button>
        {enabled && (
          <button type="button" onClick={sendNow} disabled={sending} className="ml-1 text-[11px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1" title="Send today's digest to me now" data-testid="inbox-digest-send-now">
            <PaperPlaneTilt size={12} /> {sending ? "Sending…" : "Send now"}
          </button>
        )}
      </div>
      <Button variant="outline" onClick={onPaste} data-testid="inbox-paste-btn"><ClipboardText size={15} className="mr-1.5" /> Paste an email</Button>
      <Button variant="outline" onClick={onPull} disabled={pulling || !connected} title={connected ? "Check the mailbox now" : "Connect a mailbox first"} data-testid="inbox-pull-btn">
        <ArrowsClockwise size={15} className={`mr-1.5 ${pulling ? "animate-spin" : ""}`} /> {pulling ? "Checking…" : "Check mailbox now"}
      </Button>
      <Button onClick={onSettings} data-testid="inbox-settings-btn"><Gear size={15} className="mr-1.5" /> Mailbox settings</Button>
    </div>
  );
}

function mailboxState(counts) {
  if (!counts?.connected) return { word: "Not connected", tone: "text-muted-foreground", sub: "Paste emails or connect one in settings" };
  if (!counts.enabled) return { word: "Paused", tone: "text-amber-600", sub: "Mailbox saved but polling is paused" };
  if (counts.last_poll_status === "error") return { word: "Error", tone: "text-red-600", sub: counts.last_error || "Last check failed" };
  return { word: "Polling", tone: "text-emerald-600", sub: counts.last_poll_at ? `Last check ${fmtWhen(counts.last_poll_at)}` : "Checks every minute" };
}

const CARDS = [
  { key: "new", label: "Unread (new)", count: (c) => c.new },
  { key: "urgent", label: "Urgent", count: (c) => c.urgent, sub: "open emails flagged urgent", tone: (c) => (c.urgent ? "text-amber-600" : "") },
  { key: "awaiting", label: "Awaiting reply", count: (c) => c.awaiting, sub: "no staff reply yet", tone: (c) => (c.awaiting ? "text-sky-600" : "") },
  { key: "converted", label: "Converted", count: (c) => c.converted },
  { key: "threaded", label: "Added to requests", count: (c) => c.added },
  { key: "ignored", label: "Ignored", count: (c) => c.ignored },
];

/** Seven stat cards — the first six double as filters; the last shows the mailbox state. */
export function InboxStats({ counts, view, onView }) {
  const mb = mailboxState(counts);
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-7 gap-3" data-testid="inbox-stats">
      {CARDS.map(card => {
        const active = view === card.key;
        const n = counts ? card.count(counts) : "—";
        return (
          <button key={card.key} type="button" onClick={() => onView(card.key)}
            className={`text-left rounded-lg border px-4 py-3 min-h-[88px] transition-colors ${active ? "border-primary ring-1 ring-primary/40 bg-primary/5" : "border-border bg-card hover:border-foreground/30"}`}
            aria-pressed={active} data-testid={`inbox-stat-${card.key}`}>
            <p className="text-[11px] text-muted-foreground">{card.label}</p>
            <p className={`text-2xl font-[Chivo] font-bold tabular-nums mt-1 ${counts && card.tone ? card.tone(counts) : ""}`} data-testid={`inbox-stat-${card.key}-count`}>{n}</p>
            {card.sub && <p className="text-[10px] text-muted-foreground mt-0.5">{card.sub}</p>}
          </button>
        );
      })}
      <div className="rounded-lg border border-border bg-card px-4 py-3 min-h-[88px]" data-testid="inbox-stat-mailbox">
        <p className="text-[11px] text-muted-foreground inline-flex items-center gap-1"><Envelope size={11} /> Mailbox</p>
        <p className={`text-xl font-[Chivo] font-bold mt-1 ${mb.tone}`} data-testid="inbox-stat-mailbox-word">{mb.word}</p>
        <p className="text-[10px] text-muted-foreground mt-0.5 truncate" title={mb.sub}>{mb.sub}</p>
      </div>
    </div>
  );
}

export const VIEWS = [
  { key: "new", label: "New" }, { key: "urgent", label: "Urgent" }, { key: "awaiting", label: "Awaiting reply" }, { key: "overdue", label: "Overdue reply" },
  { key: "all", label: "All" }, { key: "converted", label: "Converted" }, { key: "threaded", label: "Threaded" }, { key: "ignored", label: "Ignored" },
];

export function ViewChips({ view, counts, onView }) {
  const n = (k) => (counts && k !== "all" ? counts[k === "threaded" ? "added" : k] : null);
  return (
    <div className="flex items-center gap-1.5 flex-wrap" data-testid="inbox-view-tabs">
      {VIEWS.map(v => {
        const c = n(v.key);
        return (
          <button key={v.key} type="button" onClick={() => onView(v.key)} aria-pressed={view === v.key}
            className={`h-7 px-2.5 rounded-md border text-xs font-medium transition-colors ${view === v.key ? "border-primary bg-primary text-primary-foreground" : "border-border text-muted-foreground hover:bg-muted hover:text-foreground"} ${v.key === "overdue" && c ? "text-red-600 border-red-300" : ""}`}
            data-testid={`inbox-view-${v.key}`}>
            {v.label}{c ? <span className="ml-1 tabular-nums opacity-80" data-testid={`inbox-view-count-${v.key}`}>{c}</span> : null}
          </button>
        );
      })}
    </div>
  );
}
